import React, { useContext } from 'react'
import classNames from 'classnames'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import { ButtonProps } from './Button.d'

const Button = ({
  ariaLabel,
  arrow,
  arrowDown,
  arrowHover,
  buttonRef,
  children,
  classes,
  dataTestId,
  disabled,
  download,
  fontSize = ['text-base'],
  fontWeight = ['font-normal'],
  fullwidth,
  heapInteraction,
  href,
  id,
  isDecorative,
  numComponents = 1,
  onClick,
  playButton,
  size = 'default',
  tabIndex,
  target,
  type = 'button',
  variation = 'primary',
  withIcon
}: ButtonProps) => {
  const context = useContext(I18nextContext)
  const linkHref = localizeLink(href, context.language, context)

  // Toggle between link, span, or button depending on if we have a href prop or it's marked as decorative
  const ButtonEl = href ? 'a' : isDecorative ? 'span' : 'button'

  // Set up Tailwind CSS classes for different cases
  const buttonStyles = classNames(
    // Base button classes
    'appearance-none leading-none inline-flex items-center relative transition-all duration-300 box-border w-full',
    classes,
    {
      'justify-center text-center': variation !== 'text',
      // Variations for different colors
      'text-white bg-blue-75 border-blue-75 border-2 hover:bg-blue-100 hover:border-blue-100 rounded-md font-sansPro':
        variation === 'primary',
      'text-link bg-white rounded-md border-2 border-blue-75 hover:border-blue-100 hover:text-link-hover font-sansPro':
        variation === 'secondary',
      'text-link bg-white rounded-md hover:text-link-hover font-sansPro':
        variation === 'secondary-no-border',
      'text-white bg-none rounded-lg border-2 border-white font-sansPro':
        variation === 'inverse',
      'text-link bg-white rounded-lg border-2 border-light font-sansPro':
        variation === 'inverse-primary',
      // Primary orange button
      'py-3.5 px-6 text-white bg-brand-50': variation === 'primary-otl',
      'py-3.5 px-6 font-sansPro text-white bg-brand-50 rounded-lg border-brand-50 border-2 hover:bg-blue-100 hover:border-blue-100':
        variation === 'secondary-alt-otl',
      'py-3.5 px-6 font-sansPro leaeding-none text-white bg-blue-75 rounded-lg border-blue-75 border-2 hover:bg-blue-100 hover:border-blue-100':
        variation === 'secondary-otl',
      'py-3.5 px-6 font-sansPro bg-white text-blue-75 rounded-lg border-blue-75 border-2 hover:text-blue-100 hover:border-blue-100':
        variation === 'secondary-outline-otl',
      'py-3.5 px-6 text-white bg-legacy-blue-3 uppercase font-semibold':
        variation === 'legacy',
      'py-3 px-6 font-sansPro text-white border-solid border-2 border-white ':
        variation === 'secondary-transparent-outline-otl',
      'py-3 px-6 font-sansPro text-white after:content-icon-right-arrow-white after:ml-2 after:right-4':
        variation === 'tertiary-otl',
      'p-0 font-sansPro text-blue-75 hover:text-blue-100 after:content-icon-right-arrow-blue-75 after:ml-1':
        variation === 'text',
      'backdrop-blur-[30px] bg-white/40 font-sansPro font-semibold hover:bg-blue-75/100 px-6 py-4 rounded-[60px] text-opacity-100 text-white':
        variation === 'pill',
      // Cursor settings
      // Standard
      'cursor-pointer': !disabled,
      // Disabled
      'cursor-default': disabled,
      // Arrow classes, if we use the arrow
      // Base arrow styles
      'after:content-icon-right-arrow-white after:ml-2 after:right-5 after:inline-block':
        arrow,
      'after:content-icon-down-arrow-white after:ml-0 after:pl-2 after:pb-[5px] after:inline-block':
        arrowDown && variation === 'primary',
      'after:content-icon-down-arrow-blue after:ml-0 after:pl-2 after:pb-[5px] after:inline-block':
        arrowDown && variation === 'secondary',
      'after:content-icon-download-blue after:ml-0 after:pl-2 after:pt-[3px] after:inline-block':
        download && variation === 'secondary',
      'after:content-icon-download-white after:ml-0 after:absolute after:right-5 after:inline-block':
        download && variation !== 'secondary',
      'before:ml-0 before:absolute before:inline-flex before:items-center before:pt-[5px] before:left-[24px] !rounded-button before:h-full transform-[translateY(-48%)]':
        playButton,
      'before:content-icon-play-button-white':
        playButton && variation === 'primary',
      'before:content-icon-play-button-blue':
        playButton && variation === 'secondary',
      // If the arrow has a hover effect
      'hover:after:ml-2 hover:pr-10 after:opacity-0 hover:after:opacity-100 after:transition-all after:duration-300 after:right-4':
        arrowHover,

      //Sizing
      'py-3 px-6': size === 'default' && variation !== 'text',
      'h-14 py-5 px-10 md:h-10 md:py-3 md:px-6': size === 'small',
      'py-[14px] px-8': size === 'medium', // remove the height to accommodate safari 13.1
      'h-14 py-5 px-10': size === 'large',
      'h-14 md:h-12 py-3 px-6': size === 'largeMobile',
      '!pl-[60px]': size === 'large' && withIcon === 'before',
      'md:w-auto': !fullwidth,
      'w-full': fullwidth
    }
  )

  const buttonClasses = classNames(buttonStyles, {
    [fontSize.join(' ')]: fontSize,
    [fontWeight.join(' ')]: fontWeight
  })

  return (
    <ButtonEl
      aria-label={ariaLabel}
      className={buttonClasses}
      data-interaction={heapInteraction ?? ''}
      disabled={disabled}
      href={linkHref}
      id={id}
      onClick={onClick}
      ref={buttonRef as any}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      tabIndex={isDecorative ? -1 : tabIndex}
      target={target}
      type={isDecorative ? null : type}
      data-testid={
        dataTestId ?? `Button${numComponents ? `-${numComponents}` : ''}`
      }
    >
      {children}
    </ButtonEl>
  )
}

export default Button
